import { Controller, useForm } from 'react-hook-form';
import React, { useEffect } from 'react';
import { InputText } from '../ui';
import { InputValidationError } from '../types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import Tooltip from '@material-ui/core/Tooltip';
import Info from '../../icons/Info';
import SaveFooterLayout from '../../layouts/SaveFooterLayout';
import { Role } from '../../store/conference/types';

export interface ResearcherFormFields {
  dblp_key: string;
  google_scholar_id: string;
  orcid_id: string;
}

interface Props {
  role: Role | null;
  formData: ResearcherFormFields;
  onSave: (data: ResearcherFormFields) => void;
  onCancel?: () => void;
  validation_error?: { [k: string]: string };
  clearValidationErrorsAction: () => void;
}

const ResearcherInfoForm: React.FC<Props> = ({
  role,
  formData,
  onSave,
  onCancel,
  validation_error,
  clearValidationErrorsAction,
  children,
}) => {
  const { handleSubmit, errors, control } = useForm<ResearcherFormFields>();
  const { t } = useTranslation();

  useEffect(() => {
    return () => clearValidationErrorsAction();
  }, []);

  let dblpRequired = false;
  let googleScholarRequired = false;
  let orcidIdRequired = false;
  if (role) {
    dblpRequired = role.dblp_required;
    googleScholarRequired = role.gscholar_required;
    orcidIdRequired = role.orcid_required;
  }

  const frontend_validation_rules: { [key in keyof ResearcherFormFields]: any } = {
    dblp_key: {
      required: dblpRequired ? "This field is required. If you don't have one, please write 'Not in DBLP'." : false,
    },
    google_scholar_id: {
      required: googleScholarRequired
        ? "This field is required. If you don't have one, please write 'Not in Google Scholar'."
        : false,
    },
    orcid_id: {
      required: orcidIdRequired ? "This field is required. If you don't have one, please create it." : false,
    },
  };

  const validationErrorsByField: { [key in keyof ResearcherFormFields]: InputValidationError } = {
    dblp_key: {},
    google_scholar_id: {},
    orcid_id: {},
  };

  // Frontend Validation
  if (Object.keys(errors).length > 0) {
    Object.keys(frontend_validation_rules).forEach((key) => {
      const field = key as keyof ResearcherFormFields;
      const error = errors[field];
      if (error) {
        validationErrorsByField[field] = {
          ...validationErrorsByField[field],
          error: true,
          helperText: error.message,
        };
      }
    });
  } else {
    // Backend Validation
    if (validation_error) {
      Object.keys(frontend_validation_rules).forEach((key) => {
        const field = key as keyof ResearcherFormFields;
        const error = validation_error[field];
        if (error) {
          validationErrorsByField[field] = {
            ...validationErrorsByField[field],
            error: true,
            helperText: error,
          };
        }
      });
    }
  }

  const form = (
    <form onSubmit={handleSubmit(onSave)} className="flex flex-col mobileCtrlProfileForm" style={{ flexGrow: 1 }}>
      <StyledFormContent style={{ flexGrow: 1 }}>
        <div className="flex flex-col mx-10" style={{ width: '24rem' }}>
          <Controller
            name="dblp_key"
            control={control}
            defaultValue={formData.dblp_key}
            rules={frontend_validation_rules.dblp_key}
            render={({ onChange, value }) => (
              <div className="relative">
                <InputText
                  label="DBLP URL"
                  required={dblpRequired}
                  value={value}
                  onChange={onChange}
                  error={validationErrorsByField.dblp_key.error}
                  helperText={validationErrorsByField.dblp_key.helperText}
                  className="w-full"
                />
                <Tooltip
                  title={
                    <span style={{ fontSize: '12px' }}>
                      {t(
                        "Your DBLP URL (i.e. https://dblp.org/pid/00/0000.html or https://dblp.uni-trier.de/pid/00/0000.html or https://dblp.org/pid/s/YourName.html). Please avoid providing links to disambiguation pages (e.g., https://dblp.org/pid/50/3402.html is not acceptable, but https://dblp.org/pid/50/3402-26.html is fine). If you don't have one, please write 'Not in DBLP'",
                      )}
                    </span>
                  }
                  placement="top-start"
                >
                  <div className="absolute tooltipProfileForm tooltipDblp" style={{ top: '15px', left: '80px' }}>
                    <Info />
                  </div>
                </Tooltip>
              </div>
            )}
          />
          <Controller
            name="google_scholar_id"
            control={control}
            defaultValue={formData.google_scholar_id}
            rules={frontend_validation_rules.google_scholar_id}
            render={({ onChange, value }) => (
              <div className="relative">
                <InputText
                  label="Google Scholar URL"
                  required={googleScholarRequired}
                  value={value}
                  onChange={onChange}
                  error={validationErrorsByField.google_scholar_id.error}
                  helperText={validationErrorsByField.google_scholar_id.helperText}
                  className="w-full"
                />
                <Tooltip
                  title={
                    <span style={{ fontSize: '12px' }}>
                      {t(
                        "Your scholar profile URL (i.e. https://scholar.google.com/citations?user=MsGRzsoAAAAJ). If you don't have one, please write 'Not in Google Scholar'",
                      )}
                    </span>
                  }
                  placement="top-start"
                >
                  <div className="absolute tooltipProfileForm tooltipScholar" style={{ top: '15px', left: '139px' }}>
                    <Info />
                  </div>
                </Tooltip>
              </div>
            )}
          />
          <Controller
            name="orcid_id"
            control={control}
            defaultValue={formData.orcid_id}
            rules={frontend_validation_rules.orcid_id}
            render={({ onChange, value }) => (
              <div className="relative">
                <InputText
                  label="ORCID URL"
                  required={orcidIdRequired}
                  value={value}
                  onChange={onChange}
                  error={validationErrorsByField.orcid_id.error}
                  helperText={validationErrorsByField.orcid_id.helperText}
                  className="w-full"
                />
                <Tooltip
                  title={
                    <span style={{ fontSize: '12px' }}>
                      {t(
                        "Your ORCID ID (i.e. https://orcid.org/0000-0000-0000-0000). If you don't have one, please create it.'",
                      )}
                    </span>
                  }
                  placement="top-start"
                >
                  <div className="absolute tooltipProfileForm tooltipOrcid" style={{ top: '15px', left: '85px' }}>
                    <Info />
                  </div>
                </Tooltip>
              </div>
            )}
          />
        </div>
      </StyledFormContent>
      {children}
    </form>
  );

  return children ? (
    form
  ) : (
    <SaveFooterLayout onSave={handleSubmit(onSave)} onCancel={onCancel}>
      {form}
    </SaveFooterLayout>
  );
};

const StyledFormContent = styled.div`
  display: flex;
  margin: 0 auto 4% auto;
  max-width: 930px;

  .k-widget.k-dropdown {
    width: 24rem;
  }

  label,
  .labelSelect {
    margin-top: 15px;
    margin-bottom: 5px;
  }
  .helperText {
    height: auto;
  }
`;

export default ResearcherInfoForm;
